import type { QueryClient } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import {
  disconnectNativeVoiceSession,
  readActiveNativeVoiceSessionTarget,
  useNativeVoiceSession,
} from '#features/voice/utils/nativeVoiceSession';
import { playLeaveSound } from '#features/voice/utils/roomSounds';

export type VoiceParticipantPayload = {
  userId: string;
  /** Сейчас в голосе. Это ответ медиасервера, а не наша запись: закрывший вкладку исчезает мгновенно. */
  connected: boolean;
  /** Автор поста — он же модератор комнаты. */
  host: boolean;
  totalSeconds: number;
};

export type VoiceRoomPayload = {
  sessionId: string;
  postId: string;
  /** OPEN | CLOSED */
  status: string;
  hostId: string | null;
  maxParticipants: number | null;
  connectedCount: number;
  participants: VoiceParticipantPayload[];
  /** Заполняются только в ответе на join и только тому, кто входит. */
  livekitUrl: string | null;
  token: string | null;
};

/**
 * За сколько до старта комната сессии появляется на экране сама.
 *
 * Раньше это же число запирало голос («откроется за 10 минут до старта») и проверялось сервером.
 * С 08.09.2026 голос открыт с создания сессии, и число осталось только правилом показа: комната
 * выходит из-за края за десять минут до старта — или раньше, если я уже зашёл в её голос, тогда
 * её выводит сам голос (см. `pickCurrentSession`).
 */
export const ROOM_APPEARS_BEFORE_START_MS = 10 * 60 * 1000;

/**
 * Код отказа с бэкенда сохраняется как есть: voice.not.member, voice.room.full,
 * voice.capacity.reached и остальные. Текст к ним уже локализован на сервере, а код нужен, чтобы
 * интерфейс мог отличить временную занятость от постоянного запрета.
 */
export class VoiceRequestError extends Error {
  readonly errorCode: string;

  readonly errorMessage: string | null;

  constructor(errorCode: string, errorMessage?: string | null) {
    super(errorMessage || errorCode);
    this.errorCode = errorCode;
    this.errorMessage = errorMessage ?? null;
  }
}

const VOICE_ROOM_FIELDS = `
  sessionId
  postId
  status
  hostId
  maxParticipants
  connectedCount
  participants {
    userId
    connected
    host
    totalSeconds
  }
`;

const joinVoiceDoc = graphql(`
  mutation VoiceJoin($sessionId: UUID!) {
    voice {
      join(sessionId: $sessionId) {
        __typename
        ... on VoiceRoom {
          sessionId
          postId
          status
          hostId
          maxParticipants
          connectedCount
          participants {
            userId
            connected
            host
            totalSeconds
          }
          livekitUrl
          token
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        # У InputError нет errorCode/errorMessage — только список полей. Выбрать несуществующее
        # поле здесь значит уронить мутацию целиком на валидации, то есть закрыть вход в голос.
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`);

const leaveVoiceDoc = graphql(`
  mutation VoiceLeave($sessionId: UUID!) {
    voice {
      leave(sessionId: $sessionId) {
        __typename
        ... on VoiceRoom {
          sessionId
          postId
          status
          connectedCount
          participants {
            userId
            connected
            host
            totalSeconds
          }
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

const kickVoiceDoc = graphql(`
  mutation VoiceKick($sessionId: UUID!, $userId: UUID!) {
    voice {
      kick(sessionId: $sessionId, userId: $userId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

const muteVoiceDoc = graphql(`
  mutation VoiceMute($sessionId: UUID!, $userId: UUID!) {
    voice {
      mute(sessionId: $sessionId, userId: $userId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

// Строкой, а не через graphql(): `voice.room` возвращает голый тип без union — тот же приём, что
// у активного звонка в соседней папке.
const voiceRoomQuery = `
  query VoiceRoom($sessionId: UUID!) {
    voice {
      room(sessionId: $sessionId) {
        ${VOICE_ROOM_FIELDS}
      }
    }
  }
`;

type VoiceUnionPayload =
  | ({ __typename: 'VoiceRoom' } & VoiceRoomPayload)
  | { __typename: 'GlobalError'; errorCode?: string | null; errorMessage?: string | null }
  | {
      __typename: 'InputError';
      errors?: { name?: string | null; messagesErrors?: string[] | null }[] | null;
    }
  | { __typename?: string };

const unwrapRoom = (payload: VoiceUnionPayload | null | undefined): VoiceRoomPayload => {
  if (!payload || payload.__typename !== 'VoiceRoom') {
    if (payload?.__typename === 'InputError') {
      // У InputError код отказа не приходит вовсе — берём первое сообщение поля, чтобы человек
      // увидел причину, а не безымянную ошибку.
      const fields = (payload as { errors?: { messagesErrors?: string[] | null }[] | null }).errors;
      const first = fields?.[0];
      throw new VoiceRequestError('voice.input.error', first?.messagesErrors?.[0] ?? null);
    }
    const error = payload as
      | { errorCode?: string | null; errorMessage?: string | null }
      | undefined;
    throw new VoiceRequestError(error?.errorCode ?? 'global.error', error?.errorMessage);
  }
  const room = payload as { __typename: 'VoiceRoom' } & VoiceRoomPayload;
  return { ...room, participants: room.participants ?? [] };
};

const assertOperationOk = (
  payload:
    | { __typename?: string; errorCode?: string | null; errorMessage?: string | null }
    | null
    | undefined
) => {
  if (!payload || payload.__typename !== 'Operation') {
    throw new VoiceRequestError(payload?.errorCode ?? 'global.error', payload?.errorMessage);
  }
};

/** Вход в голос сессии. Возвращает учётные данные LiveKit — они персональные, не кэшировать. */
export const joinVoiceRoom = async (sessionId: string): Promise<VoiceRoomPayload> => {
  const data = (await gqlFetcher(joinVoiceDoc, { sessionId })) as {
    voice?: { join?: VoiceUnionPayload | null } | null;
  };
  return unwrapRoom(data.voice?.join);
};

/**
 * Явный выход. Медиа рвёт сам клиент; этот вызов нужен, чтобы остальные увидели уход сразу, не
 * дожидаясь серверной сверки.
 */
export const leaveVoiceRoom = async (sessionId: string): Promise<VoiceRoomPayload | null> => {
  const data = (await gqlFetcher(leaveVoiceDoc, { sessionId })) as {
    voice?: { leave?: VoiceUnionPayload | null } | null;
  };
  const payload = data.voice?.leave;
  // Выход из уже закрытой комнаты — не ошибка: её могла закрыть сверка, пока мы отключались.
  if (!payload || payload.__typename !== 'VoiceRoom') {
    return null;
  }
  return unwrapRoom(payload);
};

export const kickFromVoiceRoom = async (sessionId: string, userId: string): Promise<void> => {
  const data = (await gqlFetcher(kickVoiceDoc, { sessionId, userId })) as {
    voice?: { kick?: { __typename?: string; errorCode?: string | null } | null } | null;
  };
  assertOperationOk(data.voice?.kick);
};

export const muteInVoiceRoom = async (sessionId: string, userId: string): Promise<void> => {
  const data = (await gqlFetcher(muteVoiceDoc, { sessionId, userId })) as {
    voice?: { mute?: { __typename?: string; errorCode?: string | null } | null } | null;
  };
  assertOperationOk(data.voice?.mute);
};

/** Состояние комнаты без учётных данных — для карточки сессии и списков. */
export const fetchVoiceRoom = async (sessionId: string): Promise<VoiceRoomPayload | null> => {
  const data = (await gqlFetcher(voiceRoomQuery, { sessionId })) as {
    voice?: { room?: VoiceRoomPayload | null } | null;
  };
  const room = data.voice?.room;
  return room ? { ...room, participants: room.participants ?? [] } : null;
};

/**
 * Выход из голоса откуда угодно: из виджета, из карточки сессии, из отмены подключения.
 *
 * Порядок обязателен — сперва гасим стор, и только потом сообщаем серверу. Сброс стора роняет
 * эффект, который держит соединение с медиасервером, то есть человек перестаёт быть слышен
 * мгновенно; сетевой запрос после этого лишь ускоряет обновление у остальных.
 *
 * Клиент кэша передаётся снаружи: в приложении их два — свой создаёт роутер (он и стоит за
 * useQuery в карточках), а модульный синглтон живёт сам по себе. Запись в синглтон уходила в
 * никуда, и после «выйти» карточка держала прежнее «N в голосе» до перезагрузки страницы.
 */
export const leaveVoice = async (
  client: QueryClient,
  options?: { remember?: boolean; onlySessionId?: string }
) => {
  const leavingRoom = useVoiceStore.getState().room;
  const nativeTarget =
    !leavingRoom && useNativeVoiceSession.getState().mode !== 'web'
      ? await readActiveNativeVoiceSessionTarget()
      : null;
  const sessionId = leavingRoom?.sessionId ?? nativeTarget?.sessionId;
  /*
    Выход из состава сессии зовёт нас с `onlySessionId`, и это единственный путь, где уход может
    касаться не той комнаты, в которой человек сидит: состоять разрешено сразу в нескольких
    сессиях, запрещены только пересекающиеся по времени. Без этой сверки выход из завтрашней
    сессии молча выбрасывал из идущего разговора. Остальные пять путей выхода (кебаб
    комнаты, выход из аккаунта, арбитраж со звонком, нативное уведомление, отмена подключения)
    говорят именно «выйди из того, где я сейчас», и ограничение не ставят.
  */
  if (options?.onlySessionId && options.onlySessionId !== sessionId) {
    return;
  }
  // Native media survives React cleanup. Wait for capture/playback to stop before reporting leave.
  if (sessionId && useNativeVoiceSession.getState().mode !== 'web') {
    await disconnectNativeVoiceSession(sessionId, nativeTarget?.generation);
  }
  // A slow native stop must not clear a newer room joined while it was in flight.
  if (useVoiceStore.getState().room && useVoiceStore.getState().room !== leavingRoom) return;
  // `remember` ставит только арбитраж со звонком: человека вынули из комнаты не его решением, и
  // плашка «Звонок завершён» обязана знать, куда предлагать вернуться. Во всех остальных путях
  // выхода память чистим — вышедшего своими руками звать обратно некуда.
  if (options?.remember && leavingRoom) {
    useVoiceStore.getState().rememberInterruptedRoom(leavingRoom);
  } else {
    useVoiceStore.getState().forgetInterruptedRoom();
  }
  /*
    Отметку «я был в голосе» снимает любой выход через приложение: своими руками из кебаба,
    выходом из аккаунта, размонтированием хоста и арбитражем со звонком. После такого выхода
    комната не имеет права предлагать «вернуться»: человек ушёл сам либо его увёл звонок, и у
    звонка для возврата есть своя память (`interruptedRoom`) со своими словами.

    Остаётся ровно один путь, на котором отметка выживает, — тот, о котором нас никто не
    предупреждает: перезагрузка, закрытая вкладка, обрыв связи.
  */
  useVoiceStore.getState().forgetVoiceSession();
  useVoiceStore.getState().reset();
  if (!sessionId) {
    return;
  }
  // Сигнал ставится здесь, а не на кнопке: из голоса выходят пятью разными путями, включая
  // автоматический — когда человека вынимает входящий звонок. Условие про `sessionId` выше и
  // отделяет настоящий уход из комнаты от выхода из аккаунта, когда в голосе никого не было.
  void playLeaveSound();
  const room = await leaveVoiceRoom(sessionId).catch(() => null);
  // Ответ на выход несёт свежий состав — кладём его в кэш сразу. Иначе карточка сессии ещё
  // несколько секунд показывает «N в голосе», считая туда только что вышедшего.
  if (room) {
    client.setQueryData(voiceRoomQueryKey(sessionId), room);
  } else {
    await client.invalidateQueries({ queryKey: voiceRoomQueryKey(sessionId) });
  }
};

export const voiceRoomQueryKey = (sessionId: string) => ['voiceRoom', sessionId] as const;

/**
 * Шаг страховочного опроса снимка комнаты.
 *
 * Двадцать секунд — из брифа (§2 «я один?»): столько человек готов ждать ответа на главный
 * вопрос участника, и столько же стоит один запрос на сессию в минуту втрое. Меньше — платим
 * сетью за ответ, который почти всегда придёт кадром; больше — «в голосе никого» висит дольше,
 * чем длится сама попытка позвать.
 */
export const VOICE_ROOM_POLL_MS = 20_000;

type VoiceRoomQueryOptions = {
  /**
   * Опрашивать снимок, пока событие не доехало. Включает его только тот, кто НЕ в голосе: изнутри
   * комнаты правду говорит живой состав LiveKit, и лишний запрос там ничего не добавляет.
   */
  poll?: boolean;
};

export const getVoiceRoomQueryOptions = (sessionId: string, options?: VoiceRoomQueryOptions) => ({
  queryKey: voiceRoomQueryKey(sessionId),
  queryFn: () => fetchVoiceRoom(sessionId),
  // Короткая свежесть спасает от гонки между входом и первым кадром события.
  staleTime: 5_000,
  /*
    Опрос — страховка, а не источник: снимок приезжает кадром по WebSocket, и обычно первым.

    Пока опроса не было вовсе, недоехавший кадр стоил человеку неверного ответа на главный его
    вопрос: хост вошёл в голос в 19:54:45, а чужое окно и через полминуты писало «0/2 · В голосе
    никого» — и обновилось только после собственного входа и перезагрузки. Ответ «я один?» не
    имеет права зависеть от доставки одного кадра.

    В скрытой вкладке опроса нет (`refetchIntervalInBackground` по умолчанию выключен): там на
    этот вопрос никто не смотрит.
  */
  refetchInterval: options?.poll ? VOICE_ROOM_POLL_MS : (false as const),
});
